import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, useTableSort } from '../UI';
import { Button } from '../UI/base/Button';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { useLocationsSettings } from '../../hooks/useLocationsSettings';
import type { LocationSettingsRow, LocationSettingsValues } from '../../hooks/useLocationsSettings';
import { LocationCreatedWithFailedRightsError } from '../../services/locationUserService';
import { buildLocationSaveErrorMessage } from '../../services/locationSaveErrorService';
import { getApiErrorMessage } from '../../utils/errorMessageUtils';
import { useAppDispatch } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { getLocationColumns } from './locationColumns';
import { LocationFormModal } from './LocationFormModal';
import type { CopySourceOption } from './LocationCopySection';
import { DeleteLocationModal } from './DeleteLocationModal';
import { LOCATIONS_TABLE_MIN_WIDTH } from './locationsConstants';
import styles from './LocationsSettings.module.css';

export const LocationsSettings = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    rows,
    users,
    isLoading,
    isUsersLoading,
    locationsLoadError,
    usersLoadError,
    saveLocation,
    addLocation,
    deleteLocation
  } = useLocationsSettings();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<LocationSettingsRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LocationSettingsRow | null>(null);

  // No initial sort: this table opens in the order the locations hook returns.
  const { sort, handleSortChange } = useTableSort();

  const columns = useMemo(
    () =>
      getLocationColumns(t, {
        onEdit: (location) => {
          setEditingLocation(location);
          setIsFormOpen(true);
        },
        onDelete: (location) => setDeleteTarget(location)
      }),
    [t]
  );

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingLocation(null);
  };

  const handleSave = async (values: LocationSettingsValues, copyFromLocationId: string | null) => {
    const target = editingLocation;
    try {
      if (target) {
        await saveLocation(target.id, values);
      } else {
        await addLocation(values, copyFromLocationId);
      }
      handleCloseForm();
    } catch (e) {
      console.error('Failed to save location:', e);

      if (e instanceof LocationCreatedWithFailedRightsError) {
        // The location exists from here on, so the form switches to editing it
        // and stays open: pressing Save again then writes the outstanding roles
        // against the new id instead of creating a second location. Seeded from
        // the submitted values rather than looked up in `rows`, which has not
        // re-rendered with the new row yet; `editingRow` swaps in the real one
        // as soon as it does.
        setEditingLocation({
          id: e.createdLocationId,
          name: values.name.trim(),
          colorId: values.colorId,
          userRoles: values.userRoles
        });
      }

      const message = buildLocationSaveErrorMessage(e, {
        t,
        name: values.name.trim(),
        isEditing: target !== null
      });
      dispatch(showError({ message }));
    }
  };

  // Returns the promise so the confirm button stays disabled until the request
  // settles. The modal is closed only on success: leaving it open on failure is
  // what makes the error recoverable with a second click.
  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    try {
      await deleteLocation(deleteTarget.id);
      setDeleteTarget(null);
    } catch (e) {
      console.error('Failed to delete location:', e);
      dispatch(showError({ message: getApiErrorMessage(e, t('config-loc-delete-error')) }));
    }
  };

  const showEmptyState = !isLoading && !locationsLoadError && rows.length === 0;

  // The modal gets the freshest version of the row it edits: the snapshot taken
  // when Edit was clicked predates the users fetch if Edit was clicked early,
  // and its rights list would be empty (see LocationFormModal's re-seed).
  const editingRow = editingLocation
    ? (rows.find((row) => row.id === editingLocation.id) ?? editingLocation)
    : null;

  // Every other location's name, so the form can reject a duplicate. The row
  // being edited is excluded, which is what keeps "rename to the same name"
  // a valid save.
  const takenNames = rows.filter((row) => row.id !== editingRow?.id).map((row) => row.name);

  // Sources for the copy picker. No self-exclusion: the section renders only
  // when creating, so there is never a row to exclude.
  const copySources: CopySourceOption[] = rows.map((row) => ({ id: row.id, name: row.name }));

  return (
    <section>
      <SectionHeader title={t('config-tab-locations')}>
        <Button
          type="primary"
          // Disabled until both locations and users have resolved, so a form can
          // never open against a half-loaded picture.
          disabled={isLoading}
          onClick={() => {
            setEditingLocation(null);
            setIsFormOpen(true);
          }}
        >
          {t('config-loc-add')}
        </Button>
      </SectionHeader>

      {isLoading && rows.length === 0 && (
        <p className={styles.emptyState}>{t('config-loc-loading')}</p>
      )}

      {locationsLoadError && <p className={styles.emptyState}>{t('config-loc-load-error')}</p>}

      {/* Locations loaded but users did not: the table below is still usable, so
          say what is actually missing instead of blaming the locations fetch. */}
      {!locationsLoadError && usersLoadError && (
        <p className={styles.loadWarning} role="alert">
          {t('config-loc-users-load-error')}
        </p>
      )}

      {showEmptyState && <p className={styles.emptyState}>{t('config-loc-empty')}</p>}

      {rows.length > 0 && (
        <Table
          columns={columns}
          data={rows}
          getRowKey={(location) => location.id}
          minWidth={LOCATIONS_TABLE_MIN_WIDTH}
          sort={sort}
          onSortChange={handleSortChange}
        />
      )}

      {isFormOpen && (
        <LocationFormModal
          location={editingRow}
          users={users}
          takenNames={takenNames}
          hasUsersLoadError={usersLoadError !== null}
          isUsersLoading={isUsersLoading}
          copySources={copySources}
          onSave={handleSave}
          onClose={handleCloseForm}
        />
      )}

      {deleteTarget && (
        <DeleteLocationModal
          locationName={deleteTarget.name}
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </section>
  );
};
